// src/encounter/rail-v3/rail-note-targets.ts
// Where a rail row's "Add to note" may go (left-rail target 4 Nov: §4b round 4, §4d, §7.2–§7.4). A plain module: no component, no
// look, no handler. It answers ONE question for the picker the catalogue is adding upstream (§7.1) and for the rail's own handler:
// which targets does this row offer now, and, when none, why.
//
// The rules, each from the target file:
//   · Problem: Elation's five targets, in Elation's order (problem-menu screenshot, round 4). "Diagnoses" = the page's `handleAddDx`
//     (Elation's Billing). The three diagnosis targets need an ICD-10 code and are the physician's only; a nurse or allied health
//     gets HPI and ROS (§4b, §7.3).
//   · Allergy: HPI · ROS · Assessment (§4b, §7.4). Medication: HPI only (a medication "goes straight to HPI", §4b).
//   · A target that writes Assessment text is hidden while the note has no Assessment free-text box (§4d, Elation's "if
//     available"): the landing reports `absent`. Today that hides "Add to HPI, Assessment and diagnoses" and "Add to Assessment and
//     diagnoses" for a problem and "Add to Assessment" for an allergy.
//   · A target whose landing the note does not have (a role with no ROS step, a template with no HPI narrative line) is hidden the
//     same way. A target whose landing exists but cannot take it NOW (pre-visit, still loading, "Already in note") is not offered and
//     its reason is kept: when no target is left, the first such reason is the row's disabled reason.
//   · No default target (UC-30 rule 2): this module never picks one.
import type { ClinicalRole } from '@/auth/use-role';
import type { RailLandingState, RailNoteLanding } from '../lib/rail-note-piece';

/** A place one target writes to: a note line (a chart piece) or the encounter diagnoses (`handleAddDx`). */
export type RailTargetLanding = RailNoteLanding | 'diagnoses';

export type RailNoteTargetId =
  | 'hpi-assessment-diagnoses'
  | 'hpi'
  | 'ros'
  | 'assessment-diagnoses'
  | 'diagnoses'
  | 'assessment';

export interface RailNoteTarget {
  id: RailNoteTargetId;
  /** Elation's wording (round 4). */
  label: string;
  /** Every place the target writes, in the order it writes them. */
  lands: readonly RailTargetLanding[];
}

const T = (id: RailNoteTargetId, label: string, lands: readonly RailTargetLanding[]): RailNoteTarget => ({ id, label, lands });

/** Elation's five problem targets, in its order (round 4, problem-menu screenshot). */
export const PROBLEM_TARGETS: readonly RailNoteTarget[] = [
  T('hpi-assessment-diagnoses', 'Add to HPI, Assessment and diagnoses', ['hpi', 'assessment', 'diagnoses']),
  T('hpi', 'Add to HPI', ['hpi']),
  T('ros', 'Add to ROS', ['ros']),
  T('assessment-diagnoses', 'Add to Assessment and diagnoses', ['assessment', 'diagnoses']),
  T('diagnoses', 'Add to diagnoses', ['diagnoses']),
];
/** Allergy: HPI · ROS · Assessment (§4b round 4). */
export const ALLERGY_TARGETS: readonly RailNoteTarget[] = [
  T('hpi', 'Add to HPI', ['hpi']),
  T('ros', 'Add to ROS', ['ros']),
  T('assessment', 'Add to Assessment', ['assessment']),
];
/** Medication: HPI only (§4b round 4). */
export const MEDICATION_TARGETS: readonly RailNoteTarget[] = [T('hpi', 'Add to HPI', ['hpi'])];

export type RailNoteRowKind = 'problem' | 'allergy' | 'medication';

/** The rail sections whose rows have "Add to note" targets, by section id. */
export const RAIL_NOTE_SECTION_KIND: Readonly<Partial<Record<string, RailNoteRowKind>>> = {
  'active-problems': 'problem',
  allergies: 'allergy',
  medications: 'medication',
};

/** The row, as far as the targets depend on it. */
export interface RailNoteRow {
  kind: RailNoteRowKind;
  /** The record id: the duplicate check asks the landing about this record. */
  sourceId: string;
  /** A problem's ICD-10 code; the diagnosis targets need one. */
  icd10?: string | null;
}

/** The note as far as the targets depend on it: each landing's state for this record. */
export interface RailNoteState {
  landing: (landing: RailTargetLanding, sourceId: string) => RailLandingState;
}

export interface RailNoteTargets {
  /** The targets the row offers now, in order. Empty → the action is disabled with `reason`. */
  targets: RailNoteTarget[];
  /** Why no target is offered (set only when `targets` is empty). */
  reason?: string;
}

/** The reason when the row offers no target and no landing gave one (every landing is absent for this user and note). */
export const NO_TARGET_REASON = 'This note has no section that takes it';

const TARGETS: Record<RailNoteRowKind, readonly RailNoteTarget[]> = {
  problem: PROBLEM_TARGETS,
  allergy: ALLERGY_TARGETS,
  medication: MEDICATION_TARGETS,
};

/** The targets this row offers this user on this note now (rules in the file header). */
export function targetsFor(row: RailNoteRow, persona: ClinicalRole, note: RailNoteState): RailNoteTargets {
  const mayDiagnose = persona === 'physician' && !!(row.icd10 ?? '').trim();
  const targets: RailNoteTarget[] = [];
  let reason: string | undefined;
  for (const target of TARGETS[row.kind]) {
    if (target.lands.includes('diagnoses') && !mayDiagnose) continue;
    const states = target.lands.map((l) => note.landing(l, row.sourceId));
    if (states.some((s) => s.kind === 'absent')) continue;
    const blocked = states.find((s): s is Extract<RailLandingState, { kind: 'blocked' }> => s.kind === 'blocked');
    if (blocked) {
      reason ??= blocked.reason;
      continue;
    }
    targets.push(target);
  }
  return targets.length > 0 ? { targets } : { targets, reason: reason ?? NO_TARGET_REASON };
}

/** The one target a row can confirm WITHOUT a pick: only a row whose kind has a single target (a medication, §4b). Every other row
 *  waits for the clinician's pick (UC-30 rule 2: no default), so this is undefined for it even when one target is left. */
export function soleTarget(row: Pick<RailNoteRow, 'kind'>, offered: RailNoteTargets): RailNoteTarget | undefined {
  return TARGETS[row.kind].length === 1 && offered.targets.length === 1 ? offered.targets[0] : undefined;
}
